import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Utensils, ChefHat, Wine as WineIcon, Leaf } from 'lucide-react';

const Restaurant = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const highlights = [
    {
      icon: <ChefHat className="h-6 w-6" />,
      title: "Cocina de Autor",
      description: "Platos elaborados con productos locales"
    },
    {
      icon: <WineIcon className="h-6 w-6" />,
      title: "Maridajes Perfectos",
      description: "Cada plato armonizado con nuestros vinos"
    },
    {
      icon: <Leaf className="h-6 w-6" />,
      title: "Ingredientes Frescos",
      description: "De nuestra huerta y productores regionales"
    },
    {
      icon: <Utensils className="h-6 w-6" />,
      title: "Ambiente Único",
      description: "Entre viñedos con vista panorámica"
    }
  ];

  return (
    <section id="restaurant" className="py-20 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Restaurant
              <span className="block" style={{color: '#D4AF37'}}>MonfrÀ</span>
            </h2>
            <div className="w-24 h-1 mb-6" style={{background: 'linear-gradient(90deg, #D4AF37 0%, #C9A961 100%)'}}></div>
            
            <p className="text-lg text-gray-600 mb-6">
              Disfrutá de una experiencia gastronómica única en nuestro exclusivo restaurant. 
              Cada plato es una celebración de los sabores uruguayos, perfectamente armonizados 
              con nuestros vinos.
            </p>

            <p className="text-lg text-gray-600 mb-8">
              El ambiente cálido y acogedor de MonfrÀ, con sus estufas a leña y vistas a los 
              viñedos, crea el escenario perfecto para una experiencia inolvidable.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {highlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
                  className="flex items-start gap-3"
                >
                  <div className="mt-1" style={{color: '#D4AF37'}}>{highlight.icon}</div>
                  <div>
                    <h4 className="font-bold text-gray-800">{highlight.title}</h4>
                    <p className="text-sm text-gray-600">{highlight.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.button
              onClick={() => {
                const element = document.getElementById('reservation');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-white px-8 py-4 rounded-full font-bold transition-colors shadow-lg"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              data-testid="restaurant-reservar-btn"
            >
              Reservar Mesa
            </motion.button>
          </motion.div>

          {/* Right Images */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                className="relative h-64 rounded-2xl overflow-hidden shadow-xl"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Restaurant MonfrÀ"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              <motion.div
                className="relative h-64 rounded-2xl overflow-hidden shadow-xl mt-8"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1498429152472-9a433d9ddf3b?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Vinos y gastronomía"
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              <motion.div
                className="relative h-64 rounded-2xl overflow-hidden shadow-xl col-span-2"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <img
                  src="https://images.unsplash.com/photo-1561906814-23da9a8bfee0?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Ambiente acogedor"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>

            {/* Decorative Element */}
            <motion.div
              className="absolute -top-8 -right-8 w-32 h-32 bg-amber-700/10 rounded-full blur-3xl"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 4 }}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Restaurant;
